import { Link, useParams } from "react-router-dom";
import { PageHead } from "../../components/site/PageHead";
import { SiteIcon } from "../../components/site/SiteIcon";
import { usePageMeta } from "../../hooks/usePageMeta";
import { productBySlug } from "../../content/products";
import { BUSINESS, IMAGES, quotePath, SITE_TITLE_SUFFIX } from "../../content/site";

interface InDevelopmentPageProps {
  /** Screen name shown in the heading and breadcrumb, e.g. "Our work". */
  name: string;
}

/**
 * Labelled In-Development placeholder (#12311 phase-manifest convention) for an approved public
 * screen whose Story has not merged yet. Never blank, never a dead end: it says what the page is,
 * that it is coming, and offers the quote form, the phone and the home page.
 */
export default function InDevelopmentPage({ name }: InDevelopmentPageProps) {
  // /products/:slug — title the placeholder by product when the slug is a real product.
  const { slug } = useParams();
  const product = slug ? productBySlug(slug) : undefined;
  const heading = product?.name ?? name;

  usePageMeta(
    product ? `${product.name}${SITE_TITLE_SUFFIX} Toowoomba` : `${name}${SITE_TITLE_SUFFIX}`,
    product?.intro[0]?.split(/(?<=\.)\s/)[0] ?? `${name} — Greatway Patios, Toowoomba.`,
  );

  return (
    <>
      <PageHead
        crumbs={product ? [{ label: "Products", to: "/products" }, { label: product.name }] : [{ label: name }]}
        title={heading}
        bg={IMAGES.homeBanner3}
      />
      <section className="gw-sec tight">
        <div className="gw-wrap">
          <div className="gw-placeholder" role="status">
            <p className="gw-eyebrow">In development</p>
            <h2 className="gw-h2" style={{ fontSize: 30 }}>
              This page is coming soon.
            </h2>
            <p style={{ marginTop: 12 }}>
              We’re rebuilding our website and this page isn’t ready yet. In the meantime, we’re happy
              to help directly.
            </p>
            <div className="gw-actions">
              <Link className="gw-btn gw-btn-red" to={quotePath(product?.slug)}>
                Get a free quote <SiteIcon name="arrow" size={18} />
              </Link>
              <a className="gw-btn gw-btn-line" href={BUSINESS.tel}>
                <SiteIcon name="phone" size={18} /> {BUSINESS.phone}
              </a>
              <Link className="gw-btn gw-btn-line" to="/">
                Back to home
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
